import React, { useState, useEffect } from "react";
import { UserIcon } from "@heroicons/react/24/outline";
import { RLink } from "../DesignSystem/RLink";

type Listener = {
  visitorId: string;
  username: string | null;
  avatarUrl: string | null;
};

export function SyncListenListeners({
  compoId,
}: {
  compoId: number;
}): JSX.Element {
  const [listeners, setListeners] = useState<Listener[]>([]);

  useEffect(() => {
    const fetchListeners = async () => {
      try {
        const response = await fetch(`/api/compo/${compoId}/sync/listeners`);
        if (response.ok) {
          const data = await response.json();
          setListeners(data.users || []);
        }
      } catch (err) {
        console.error("Failed to fetch listeners:", err);
      }
    };

    // Fetch immediately
    fetchListeners();

    // Then poll every 5 seconds
    const interval = setInterval(fetchListeners, 5000);

    return () => clearInterval(interval);
  }, [compoId]);

  // Dedupe by username (keep first occurrence) and sort: logged-in users first, then anonymous
  const displayListeners = listeners.reduce<Listener[]>((acc, listener) => {
    if (listener.username) {
      // Only add if we haven't seen this username yet
      if (!acc.some((l) => l.username === listener.username)) {
        acc.push(listener);
      }
    } else {
      acc.push(listener);
    }
    return acc;
  }, []);

  const sortedListeners = [...displayListeners].sort((a, b) => {
    if (a.username && !b.username) return -1;
    if (!a.username && b.username) return 1;
    return 0;
  });

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="p-4">
        <div className="mb-3 text-xs font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400">
          {sortedListeners.length} {sortedListeners.length === 1 ? "listener" : "listeners"}
        </div>
        <div className="space-y-2">
          {sortedListeners.map((listener) => (
            <div
              key={listener.visitorId}
              className="flex items-center gap-3 rounded-lg px-2 py-1.5"
            >
              {listener.avatarUrl ? (
                <img
                  src={listener.avatarUrl}
                  alt=""
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700">
                  <UserIcon className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                </div>
              )}
              {listener.username ? (
                <RLink
                  to={`/user/${listener.username}`}
                  className="text-sm font-medium text-slate-700 hover:text-slate-900 dark:text-slate-200 dark:hover:text-white"
                >
                  {listener.username}
                </RLink>
              ) : (
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Anonymous
                </span>
              )}
            </div>
          ))}
          {sortedListeners.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              No listeners connected
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
